import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { LocaleProvider } from "./lib/i18n";
import { queryClient } from "./lib/query-client";
import Login from "./routes/auth/Login";
import Register from "./routes/auth/Register";
import AppLayout from "./routes/app/AppLayout";
import Dashboard from "./routes/app/Dashboard";
import Home from "./routes/app/Home";
import CampaignWizard from "./routes/app/CampaignWizard";
import CampaignDetail from "./routes/app/CampaignDetail";
import Research from "./routes/app/Research";
import ResearchDetail from "./routes/app/ResearchDetail";
import BrandingScan from "./routes/app/BrandingScan";
import Settings from "./routes/app/Settings";
import { OperatorLanding, OperatorTenantApprovals } from "./routes/app/Operator";
import PostLoginLanding from "./routes/app/PostLoginLanding";
import Budget from "./routes/app/Budget";
import { CampaignsList, CampaignCreate } from "./routes/app/Campaigns";
import Approvals from "./routes/app/Approvals";
import Assets from "./routes/app/Assets";
import Assistant from "./routes/app/Assistant";
import Creative from "./routes/app/Creative";
import {
  Content,
} from "./routes/app/Placeholders";
import Reports from "./routes/app/Reports";
import Onboarding from "./routes/app/Onboarding";
import { LandingPagesList, LandingPageDetail } from "./routes/app/LandingPages";
import { LandingPagesV2List, LandingPageV2Detail } from "./routes/app/LandingPagesV2";
import ProgrammaticSEO from "./routes/app/ProgrammaticSEO";
import Lifecycle from "./routes/app/Lifecycle";
import SalesOrbit from "./routes/app/SalesOrbit";
import { ExperimentsList, ExperimentDetail, ExperimentCreate } from "./routes/app/Experiments";
import { ContentCalendar } from "./routes/app/Content";
import Autopilot from "./routes/app/Autopilot";
import AutopilotRunDetail from "./routes/app/AutopilotRunDetail";
import AutopilotActions from "./routes/app/AutopilotActions";
import AgentRunInspector from "./routes/app/AgentRunInspector";
import WorkflowHistory from "./routes/app/WorkflowHistory";
import WorkflowInstanceDetail from "./routes/app/WorkflowInstanceDetail";
import ApprovalChains from "./routes/app/ApprovalChains";
import ApprovalChainDetail from "./routes/app/ApprovalChainDetail";
import Cockpit from "./routes/app/Cockpit";
import CampaignCockpitDetail from "./routes/app/CampaignCockpitDetail";
import AdSpend from "./routes/app/AdSpend";
import Integrations from "./routes/app/Integrations";
import GuidedIntegrationCenter from "./routes/app/GuidedIntegrationCenter";
import ConnectionEditor from "./routes/app/ConnectionEditor";
import DeadLetterQueue from "./routes/app/DeadLetterQueue";
import Audit from "./routes/app/Audit";
import Quality from "./routes/app/Quality";
import SettingsLegal from "./routes/app/SettingsLegal";
import CampaignTemplatesAdmin from "./routes/app/CampaignTemplatesAdmin";
import QualityRubrics from "./routes/app/QualityRubrics";
import StrategyMemory from "./routes/app/StrategyMemory";
import KnowledgeGraph from "./routes/app/KnowledgeGraph";
import AdPlatforms from "./routes/app/AdPlatforms";
import DiscoveryGrowth from "./routes/app/DiscoveryGrowth";
import LeadsList, { LeadDetail } from "./routes/app/Leads";
import GoalsList, { GoalNew, GoalDetail } from "./routes/app/Goals";
import OrchestratorRunDetail from "./routes/app/OrchestratorRun";
import CampaignPreview from "./routes/app/CampaignPreview";
import OperatorLayout from "./routes/operator/OperatorLayout";
import OperatorDashboard from "./routes/operator/OperatorDashboard";
import OperatorTenants from "./routes/operator/OperatorTenants";
import OperatorTenantDetail from "./routes/operator/OperatorTenantDetail";
import OperatorWeeklyReports from "./routes/operator/OperatorWeeklyReports";
import OperatorSettings from "./routes/operator/OperatorSettings";
import OperatorPlans from "./routes/operator/OperatorPlans";
import { OperatorFeatureFlags, OperatorOperators }
  from "./routes/operator/OperatorPhase3";
import OperatorAudit from "./routes/operator/OperatorAudit";
import OperatorHealth from "./routes/operator/OperatorHealth";
import TenantSubscription from "./routes/app/TenantSubscription";
import LLMSetupStep from "./routes/app/LLMSetupStep";

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/" element={<Navigate to="/app/home" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Session 7 Phase 1 — Platform Operator console (top-level). */}
            <Route path="/operator" element={<OperatorLayout />}>
              <Route index element={<Navigate to="/operator/dashboard" replace />} />
              <Route path="dashboard" element={<OperatorDashboard />} />
              <Route path="tenants" element={<OperatorTenants />} />
              <Route path="tenants/:id" element={<OperatorTenantDetail />} />
              <Route path="approvals" element={<OperatorTenantApprovals />} />
              <Route path="weekly-reports" element={<OperatorWeeklyReports />} />
              <Route path="plans" element={<OperatorPlans />} />
              <Route path="feature-flags" element={<OperatorFeatureFlags />} />
              <Route path="operators" element={<OperatorOperators />} />
              <Route path="audit" element={<OperatorAudit />} />
              <Route path="health" element={<OperatorHealth />} />
              <Route path="settings" element={<OperatorSettings />} />
            </Route>
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<PostLoginLanding />} />
              <Route path="home" element={<Home />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="campaigns/wizard" element={<CampaignWizard />} />
              <Route path="campaigns/:id" element={<CampaignDetail />} />
              <Route path="research" element={<Research />} />
              <Route path="research/:id" element={<ResearchDetail />} />
              <Route path="branding-scan" element={<BrandingScan />} />
              <Route path="onboarding/llm" element={<LLMSetupStep />} />
              <Route path="settings/subscription" element={<TenantSubscription />} />
              <Route path="assistant" element={<Assistant />} />
              <Route path="campaigns" element={<CampaignsList />} />
              <Route path="campaigns/new" element={<CampaignCreate />} />
              <Route path="campaigns/:brief_id/preview" element={<CampaignPreview />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="approval-chains" element={<ApprovalChains />} />
              <Route path="approval-chains/:id" element={<ApprovalChainDetail />} />
              <Route path="cockpit" element={<Cockpit />} />
              <Route path="cockpit/campaigns/:id" element={<CampaignCockpitDetail />} />
              <Route path="ad-spend" element={<AdSpend />} />
              <Route path="integrations/crm/dead-letter-queue" element={<DeadLetterQueue />} />
              <Route path="integrations/crm/connections/:id" element={<ConnectionEditor />} />
              <Route path="creative" element={<Creative />} />
              <Route path="assets" element={<Assets />} />
              <Route path="landing-pages" element={<LandingPagesV2List />} />
              <Route path="landing-pages/programmatic" element={<ProgrammaticSEO />} />
              <Route path="landing-pages/legacy" element={<LandingPagesList />} />
              <Route path="landing-pages/legacy/:id" element={<LandingPageDetail />} />
              <Route path="experiments" element={<ExperimentsList />} />
              <Route path="experiments/new" element={<ExperimentCreate />} />
              <Route path="experiments/:id" element={<ExperimentDetail />} />
              <Route path="content" element={<ContentCalendar />} />
              <Route path="lifecycle" element={<Lifecycle />} />
              <Route path="sales-orbit" element={<SalesOrbit />} />
              <Route path="discovery-growth" element={<DiscoveryGrowth />} />
              <Route path="leads" element={<LeadsList />} />
              <Route path="leads/:id" element={<LeadDetail />} />
              <Route path="goals" element={<GoalsList />} />
              <Route path="goals/new" element={<GoalNew />} />
              <Route path="goals/:id" element={<GoalDetail />} />
              <Route path="orchestrator/runs/:id" element={<OrchestratorRunDetail />} />
              <Route path="landing-pages/:id" element={<LandingPageV2Detail />} />
              <Route path="content" element={<Content />} />
              <Route path="integrations" element={<GuidedIntegrationCenter />} />
              <Route path="autopilot" element={<Autopilot />} />
              <Route path="autopilot/runs/:id" element={<AutopilotRunDetail />} />
              <Route path="autopilot/actions" element={<AutopilotActions />} />
              <Route path="agents/runs/:id" element={<AgentRunInspector />} />
              <Route path="workflows" element={<WorkflowHistory />} />
              <Route path="workflows/:id" element={<WorkflowInstanceDetail />} />
              <Route path="reports" element={<Reports />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="settings" element={<Settings />} />
              <Route path="operator" element={<OperatorLanding />} />
              <Route path="operator/tenant-approvals" element={<OperatorTenantApprovals />} />
              <Route path="settings/legal" element={<SettingsLegal />} />
              <Route path="settings/budget" element={<Budget />} />
              <Route path="campaign-templates" element={<CampaignTemplatesAdmin />} />
              <Route path="quality/rubrics" element={<QualityRubrics />} />
              <Route path="strategy-memory" element={<StrategyMemory />} />
              <Route path="knowledge-graph" element={<KnowledgeGraph />} />
              <Route path="integrations/ad-platforms" element={<AdPlatforms />} />
              <Route path="audit" element={<Audit />} />
              <Route path="quality" element={<Quality />} />
            </Route>
            <Route path="*" element={<Navigate to="/app/home" replace />} />
          </Routes>
        </BrowserRouter>
      </LocaleProvider>
    </QueryClientProvider>
  );
}
